// src/WorkflowScreen.jsx

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { WorkflowCacheProvider, useWorkflowCache } from './user/contexts/WorkflowCacheContext';
import WorkflowViewer from './user/components/WorkflowViewer';
import LoadingView from './shared/components/LoadingView';
import ErrorView from './user/components/errors/ErrorView';
import { fetchWithAuth } from './admin/auth/fetchWithAuth';
import { API_URL } from './config/env';

function WorkflowScreenAdminInner() {
  const { flow_id, viewId } = useParams();
  const navigate = useNavigate();
  const [workflow, setWorkflow] = useState(null);
  const [hasForm, setHasForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  
  const { 
    ensureTaskSnapshot, 
    ensureAllFormDefinitions,
    clearTaskNamespace
  } = useWorkflowCache();

  useEffect(() => {
    const url = `${API_URL}/v1/admin/workflows/${flow_id}`;

    fetchWithAuth(`${url}`)
      .then((res) => res.json())
      .then((data) => {
        const workflowSteps = data.resolved_workflow_steps || data.workflow_steps;
        setWorkflow(workflowSteps);
        
        // Preview mode reuses a stable namespace ("preview"/"preview"),
        // so clear stale data before taking a fresh snapshot.
        clearTaskNamespace({
          accessKey: "preview",
          taskId: "preview",
        });

        // Initialize workflow cache with a forced fresh snapshot.
        ensureTaskSnapshot({ 
          accessKey: "preview", 
          taskId: "preview", 
          workflow: workflowSteps,
          force: true,
        });
      })
      .catch((err) => console.error('Error fetching workflow.json', err));
  }, [flow_id, ensureTaskSnapshot, clearTaskNamespace]);

  // Load form definitions when workflow is available (do not depend on formLoading/formError — causes reload loop / flicker).
  useEffect(() => {
    if (!workflow) return;

    let cancelled = false;

    const loadForms = async () => {
      setFormLoading(true);
      setFormError(null);

      try {
        const result = await ensureAllFormDefinitions({
          accessKey: "preview",
          taskId: "preview",
        });

        if (cancelled) return;
        if (!result.ok) {
          setFormError(result.error);
        }
      } catch (err) {
        if (!cancelled) setFormError(err);
      } finally {
        if (!cancelled) setFormLoading(false);
      }
    };

    loadForms();
    return () => {
      cancelled = true;
    };
  }, [workflow, ensureAllFormDefinitions]);

  function handleNavigate(targetViewId) {
    // Navigate to the next view
    navigate(`../${targetViewId}`, { relative: 'path' });
  }

  // Called when user explicitly clicks "Save as Draft" on the same page
  function handleSaveDraft() {
    alert('Demo mode, saving disabled');
  }

  // Called when user clicks "Save & Complete Task"
  function handleCompleteTask() {
    alert('Demo mode, saving disabled');
  }

  if (!workflow) {
    return <LoadingView />;
  }

  if (formLoading) {
    return <LoadingView title="Loading Forms..." subtitle="Please wait while we load the form definitions." />;
  }

  if (formError) {
    return (
      <ErrorView 
        title="Form Loading Error"
        message={`Failed to load form definitions: ${formError.message}`}
        onRetry={() => window.location.reload()}
        onGoBack={() => window.history.back()}
      />
    );
  }

  const currentView = workflow.find((view) => view.id === viewId);
  if (!currentView) {
    return <ErrorView 
      title = 'View not found'
      message = "We couldn't find the view you are looking for."
      onRetry={() => window.location.reload()}
      onGoBack={() => window.history.back()}
    />;
  }

  // Determine if last page => no actions
  const isLastPage = !currentView.actions || currentView.actions.length === 0;

  return (
    <div>
        <WorkflowViewer
          workflow={workflow}
          preview_mode={true}
          task_id="preview"
          accessKeyToken="preview"
          onNavigate={handleNavigate}
          onFormValidChange={(isValid) => setHasForm(isValid)}
        />
      
      {/* Additional Buttons: "Save as Draft" if hasForm, "Save & Complete" if last page */}
      <div className="container mt-3">
        {hasForm && (
          <button className="btn btn-sm btn-secondary me-2" onClick={handleSaveDraft}>
            Save as Draft
          </button>
        )}
        {isLastPage && (
          <button className="btn btn-primary" onClick={handleCompleteTask}>
            Save &amp; Complete Task
          </button>
        )}
      </div>
    </div>
  );
}

function WorkflowScreenAdmin() {
  return (
    <WorkflowCacheProvider>
      <WorkflowScreenAdminInner />
    </WorkflowCacheProvider>
  );
}

export default WorkflowScreenAdmin;
